import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { IApiSmpGptAgent } from "@smp/api";
import {
  UiButton,
  UiStep,
  UiStepLabel,
  UiStepper,
  UiTypography,
} from "@smp/ui";
import { IconChevronRight, IconWand } from "@tabler/icons-react";
import { Formik, FormikProps } from "formik";
import { FormikConfig } from "formik/dist/types";
import {
  FC,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useUnmount, useUpdateEffect } from "react-use";
import * as Yup from "yup";

import { EPromptKey } from "@/hooks/use_prompt_fetcher";
import { useMutationGptAgentCreate } from "@/queries";
import { dialogAdvisorConfig } from "@/screens/app/components/dialogs/dialog_advisor";
import { Disclaimer } from "@/screens/app/components/dialogs/dialog_advisor/components/disclaimer";
import { INITIAL_AGENT_PARAMS_CONTEXT } from "@/screens/app/components/dialogs/dialog_advisor/fixtures/agent_params_context";
import { INITIAL_AGENT_PARAMS_POST } from "@/screens/app/components/dialogs/dialog_advisor/fixtures/agent_params_post";
import { generateAgentContext } from "@/screens/app/components/dialogs/dialog_advisor/helpers/generate_agent_context";
import { useGptAsk } from "@/screens/app/components/dialogs/dialog_advisor/hooks/use_gpt_ask";
import { metrikaCombinedGoal } from "@/utils";

import { BackButton } from "./components/back_button";
import { Preloader } from "./components/preloader";
import { RadioGroup } from "./components/radio_group";
import classes from "./create_form.module.css";
import {
  EStep,
  ICreateFormProps,
  ICreateFormValues,
} from "./create_form.types";
import { StepConfirmation } from "./screens/step_confirmation";
import { StepOccupation } from "./screens/step_occupation";

export const CreateForm: FC<ICreateFormProps> = ({
  onChangeStep,
  onChangeState,
  onSuccess,
}) => {
  const { i18n } = useLingui();
  const formRef = useRef<FormikProps<ICreateFormValues> | null>(null);

  const gptAsk = useGptAsk();
  const { mutateAsync: createAgent } = useMutationGptAgentCreate({
    onSuccess: () => {
      metrikaCombinedGoal("gpt_agent_create", { advisor: 1 });
    },
  });

  const buttonsRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(EStep.OCCUPATION);
  const [targetAudienceOptions, setTargetAudienceOptions] = useState<string[]>(
    [],
  );
  const [problemOptions, setProblemOptions] = useState<string[]>([]);

  const steps = useMemo(
    () => Object.keys(EStep).filter((step) => !isNaN(Number(step))),
    [],
  );

  const validationSchema = useMemo<
    NonNullable<FormikConfig<ICreateFormValues>["validationSchema"]>
  >(
    () =>
      Yup.object().shape({
        middleData: Yup.object().shape({
          occupation: Yup.string()
            .trim()
            .required(t`This field cannot be blank`)
            .max(
              dialogAdvisorConfig.ask_input_max_length,
              t`Max. length is ${dialogAdvisorConfig.ask_input_max_length} characters`,
            ),
          target_audience: [
            EStep.TARGET_AUDIENCE,
            EStep.PROBLEM,
            EStep.CONFIRMATION,
          ].includes(currentStep)
            ? Yup.string()
                .trim()
                .required(t`This field cannot be blank`)
                .max(
                  dialogAdvisorConfig.ask_input_max_length,
                  t`Max. length is ${dialogAdvisorConfig.ask_input_max_length} characters`,
                )
            : Yup.string().notRequired(),
          problem: [EStep.PROBLEM, EStep.CONFIRMATION].includes(currentStep)
            ? Yup.string()
                .trim()
                .required(t`This field cannot be blank`)
                .max(
                  dialogAdvisorConfig.ask_input_max_length,
                  t`Max. length is ${dialogAdvisorConfig.ask_input_max_length} characters`,
                )
            : Yup.string().notRequired(),
        }),
      }),
    [currentStep],
  );

  const handleClickBack = useCallback(() => {
    setCurrentStep((state) =>
      Math.min(Math.max(0, state - 1), steps.length - 1),
    );
  }, [steps.length]);

  const hanldeSubmit = useCallback<
    NonNullable<FormikConfig<ICreateFormValues>["onSubmit"]>
  >(
    async (values, formikHelpers) => {
      onChangeState("submitting");

      const gptAskData = {
        ...values.middleData,
        language: values.post.language,
      };

      if (currentStep === EStep.OCCUPATION) {
        setTargetAudienceOptions(
          await gptAsk<string[]>(EPromptKey.ADVISOR_ASK_AUDIENCE, gptAskData),
        );
        setCurrentStep(1);
      } else if (currentStep === EStep.TARGET_AUDIENCE) {
        setProblemOptions(
          await gptAsk<string[]>(EPromptKey.ADVISOR_ASK_PROBLEMS, gptAskData),
        );
        setCurrentStep(2);
      } else if (currentStep === EStep.PROBLEM) {
        const data = await gptAsk<{
          niche: string;
          target_audience: string;
          problem: string;
          occupation: string;
        }>(EPromptKey.ADVISOR_ASK_CONTEXT, gptAskData);

        formikHelpers.setFieldValue("context", data);
        setCurrentStep(3);
      } else if (currentStep === EStep.CONFIRMATION) {
        const agentParams: IApiSmpGptAgent["params"] = {
          socialPageGroupId: values.projectId,
          context: values.context,
          post: values.post,
        };

        const contextString = generateAgentContext(agentParams);

        const newAgent = await createAgent({
          context: contextString,
          params: agentParams,
        });

        onSuccess(newAgent);
      }

      onChangeState("idle");
    },
    [createAgent, currentStep, gptAsk, onChangeState, onSuccess],
  );

  useUpdateEffect(() => {
    if (formRef.current) {
      formRef.current.validateForm();
    }
  }, [currentStep]);

  useLayoutEffect(() => {
    onChangeStep?.(currentStep);
  }, [currentStep, onChangeStep]);

  useUnmount(() => {
    onChangeStep?.(undefined);
  });

  return (
    <>
      {currentStep === EStep.OCCUPATION && (
        <UiTypography variant="body2" gutterBottom gutterBottomSize="lg">
          <Trans>Create your first set of settings in 4 steps</Trans>
        </UiTypography>
      )}

      <Formik<ICreateFormValues>
        innerRef={formRef}
        initialValues={{
          middleData: {
            occupation: "",
            target_audience: "",
            problem: "",
          },
          post: {
            ...INITIAL_AGENT_PARAMS_POST,
            language: i18n.locale,
          },
          context: INITIAL_AGENT_PARAMS_CONTEXT,
        }}
        isInitialValid={false}
        validationSchema={validationSchema}
        onSubmit={hanldeSubmit}
      >
        {({ handleSubmit, isSubmitting, isValid }) => (
          <form onSubmit={handleSubmit}>
            <div className={classes.container}>
              <UiStepper activeStep={currentStep} className={classes.stepper}>
                {steps.map((step) => (
                  <UiStep key={step}>
                    <UiStepLabel />
                  </UiStep>
                ))}
              </UiStepper>

              <div className={classes.form}>
                {currentStep === EStep.OCCUPATION && (
                  <StepOccupation disabled={isSubmitting} />
                )}

                {currentStep === EStep.TARGET_AUDIENCE &&
                  !!targetAudienceOptions.length && (
                    <RadioGroup
                      name="middleData.target_audience"
                      options={targetAudienceOptions}
                      title={<Trans>Who is your client?</Trans>}
                      buttonsRef={buttonsRef}
                      placeholder={t`__advisor.create_agent.form.placeholder.target_audience`}
                      disabled={isSubmitting}
                    />
                  )}

                {currentStep === EStep.PROBLEM && !!problemOptions.length && (
                  <RadioGroup
                    name="middleData.problem"
                    options={problemOptions}
                    title={
                      <Trans>
                        What customer problems does your business solve?
                      </Trans>
                    }
                    buttonsRef={buttonsRef}
                    placeholder={t`__advisor.create_agent.form.placeholder.problem`}
                    disabled={isSubmitting}
                  />
                )}

                {currentStep === EStep.CONFIRMATION && (
                  <StepConfirmation disabled={isSubmitting} />
                )}
              </div>
            </div>

            {currentStep === EStep.CONFIRMATION && <Disclaimer mt={3} />}

            {isSubmitting ? (
              <Preloader step={currentStep} />
            ) : (
              <div className={classes.buttons} ref={buttonsRef}>
                <BackButton
                  currentStep={currentStep}
                  onClick={handleClickBack}
                  isLoading={isSubmitting}
                />

                <div className={classes.buttons__sep} />

                {currentStep !== EStep.CONFIRMATION ? (
                  <UiButton
                    variant="contained"
                    endIcon={<IconChevronRight />}
                    size="large"
                    type="submit"
                    disabled={!isValid}
                  >
                    <Trans>Next</Trans>
                  </UiButton>
                ) : (
                  <UiButton
                    variant="contained"
                    color="success"
                    endIcon={<IconWand />}
                    size="large"
                    type="submit"
                    disabled={!isValid}
                    onClick={() =>
                      metrikaCombinedGoal("advisor_click_generate_posts", {
                        advisor: 1,
                      })
                    }
                  >
                    <Trans>Generate posts</Trans>
                  </UiButton>
                )}
              </div>
            )}
          </form>
        )}
      </Formik>
    </>
  );
};
